import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import DentistEnrollmentIntro from "./DentistEnrollmentIntro";
import DentistEnrollmentPracticeInfo from "./DentistEnrollmentPracticeInfo";
import DentistEnrollmentServiceInfo from "./DentistEnrollmentServiceInfo";
import EnrollmentSuccessMessage from "./EnrollmentSuccessMessage";
import { SignUpDentist } from "../../helpers/requests";
import { useDispatch } from "react-redux";
import {
  hideLoadingSpinner,
  showLoadingSpinner
} from "../../redux/actions/loadingSpinner";
import { showAlertDialouge, isPhoneNumberValid } from "../../helpers/utils";

const Dental_Enrollment_Steps = {
  intro: 1,
  practiceInfo: 2,
  serviceInfo: 3,
  enrollmentSuccessMessage: 4
};

const DentistSignUpForm = () => {
  const [step, setStep] = useState(Dental_Enrollment_Steps.intro);
  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const onReturnHome = () => {
    navigate("/find-dentist");
  };

  const [formData, setFormData] = useState({
    practiceName: "",
    primaryDentistName: "",
    streetAddress: "",
    city: "",
    state: "",
    zipCode: "",
    phone: "",
    email: "",
    website: "",
    npi: "",
    system: "",
    practiceTypes: [],
    insurances: [],
    languages: "",
    doNotContact: false,
    contactName: "",
    contactRole: "",
    contactTime: "",
    photos: []
  });

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);

      setFormData((prev) => {
        const combinedFiles = [...prev.photos, ...newFiles];

        if (combinedFiles.length > 3) {
          showAlertDialouge("Message", "You can only upload up to 3 photos.");
          return prev;
        }
        return { ...prev, photos: combinedFiles };
      });

      e.target.value = "";
    }
  };

  const handleRemoveFile = (indexToRemove) => {
    setFormData((prev) => ({
      ...prev,
      photos: prev.photos.filter((_, index) => index !== indexToRemove)
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }

    if (type === "checkbox" && name === "doNotContact") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleMultiSelect = (e, fieldName) => {
    const { value, checked } = e.target;

    if (errors[fieldName]) {
      setErrors((prev) => ({ ...prev, [fieldName]: null }));
    }

    setFormData((prev) => {
      const list = prev[fieldName];
      if (checked) return { ...prev, [fieldName]: [...list, value] };
      return { ...prev, [fieldName]: list.filter((item) => item !== value) };
    });
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.practiceName.trim())
      newErrors.practiceName = "Practice Name is required";
    if (!formData.primaryDentistName.trim())
      newErrors.primaryDentistName = "Dentist Name is required";
    if (!formData.streetAddress.trim())
      newErrors.streetAddress = "Address is required";
    if (!formData.city.trim()) newErrors.city = "City is required";
    if (!formData.zipCode.trim()) newErrors.zipCode = "Zip Code is required";
    if (!formData.npi.trim()) newErrors.npi = "NPI is required";

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone is required";
    } else if (!isPhoneNumberValid(formData.phone)) {
      newErrors.phone = "Invalid phone number";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (formData.practiceTypes.length === 0) {
      newErrors.practiceTypes = "Please select at least one practice type";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (step == Dental_Enrollment_Steps.intro || validateStep1()) {
      setStep((prev) => prev + 1);
    }
    window.scrollTo(0, 0);
  };

  const prevStep = () => {
    setStep((prev) => prev - 1);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep2()) {
      window.scrollTo(0, 0);
      return;
    }

    const insuranceMap = {};
    formData.insurances.forEach((ins) => {
      const key = ins.toLowerCase().replace(" dental", "").replace(/\s+/g, "_");
      insuranceMap[key] = true;
    });

    const payload = {
      practice_name: formData.practiceName,
      primary_dentist_name: formData.primaryDentistName,
      street_address: formData.streetAddress,
      city: formData.city,
      state: formData.state,
      zip_code: formData.zipCode,
      phone_number: formData.phone,
      email: formData.email,
      website_or_booking_link: formData.website,
      npi_number: formData.npi,
      practice_management_system: formData.system,
      practice_types: formData.practiceTypes,

      insurance_accepted: insuranceMap,

      languages_spoken: formData.languages,

      // Assuming you handle file uploads separately or return URLs here.
      // For now, sending empty array as placeholder if no files logic exists yet.
      photos: [],

      contact_for_followup: {
        contact_name: formData.contactName,
        contact_role: formData.contactRole,
        best_time_to_reach: formData.contactTime
      },

      do_not_contact: formData.doNotContact
    };

    dispatch(showLoadingSpinner());

    SignUpDentist(
      JSON.stringify(payload),
      function (response) {
        if (response.success) {
          setStep(Dental_Enrollment_Steps.enrollmentSuccessMessage);
        } else {
          showAlertDialouge("Message", "Failed to register. Please try again!");
        }

        dispatch(hideLoadingSpinner());
      },
      function (err) {
        handleApiErrors(err);
        dispatch(hideLoadingSpinner());
      }
    );
  };

  const progressWidth =
    step === Dental_Enrollment_Steps.serviceInfo ? "100%" : "50%";

  return (
    <div className="pi-page-wrapper min-vh-100 d-flex flex-column align-items-center py-5 bg-light">
      {step === Dental_Enrollment_Steps.intro && (
        <DentistEnrollmentIntro onStart={nextStep} />
      )}

      {step === Dental_Enrollment_Steps.enrollmentSuccessMessage && (
        <EnrollmentSuccessMessage onReturnHome={onReturnHome} />
      )}

      {step !== Dental_Enrollment_Steps.intro &&
        step !== Dental_Enrollment_Steps.enrollmentSuccessMessage && (
          <>
            <div
              className="d-flex align-items-center justify-content-center mb-4 w-100"
              style={{ maxWidth: "500px" }}
            >
              <div
                className={`d-flex align-items-center fw-bold ${
                  step === Dental_Enrollment_Steps.practiceInfo
                    ? "text-dark"
                    : "text-muted"
                }`}
              >
                <div
                  className={`pi-step-icon d-flex align-items-center justify-content-center rounded-circle me-2 text-white ${
                    step === Dental_Enrollment_Steps.practiceInfo
                      ? "pi-step-active"
                      : "pi-step-active"
                  }`}
                >
                  {step === Dental_Enrollment_Steps.practiceInfo ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-building2 w-5 h-5"
                    >
                      <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"></path>
                      <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"></path>
                      <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"></path>
                      <path d="M10 6h4"></path>
                      <path d="M10 10h4"></path>
                      <path d="M10 14h4"></path>
                      <path d="M10 18h4"></path>
                    </svg>
                  ) : (
                    "✓"
                  )}
                </div>
                <span>Practice Info</span>
              </div>

              <div
                className="flex-grow-1 mx-3"
                style={{
                  height: "2px",
                  backgroundColor: "#e0e0e0",
                  position: "relative"
                }}
              >
                <div
                  style={{
                    width:
                      step === Dental_Enrollment_Steps.serviceInfo
                        ? "100%"
                        : "0%",
                    height: "100%",
                    backgroundColor: "#d6258f",
                    transition: "width 0.3s"
                  }}
                ></div>
              </div>

              <div
                className={`d-flex align-items-center fw-bold ${
                  step === Dental_Enrollment_Steps.serviceInfo
                    ? "text-dark"
                    : "text-muted"
                }`}
              >
                <div
                  className={`pi-step-icon d-flex align-items-center justify-content-center rounded-circle me-2 ${
                    step === Dental_Enrollment_Steps.serviceInfo
                      ? "pi-step-active text-white"
                      : "bg-secondary bg-opacity-25 text-secondary"
                  }`}
                >
                  📄
                </div>
                <span>Services & Details</span>
              </div>
            </div>

            <div
              className="w-100 mb-4"
              style={{
                maxWidth: "800px",
                height: "6px",
                backgroundColor: "#e9ecef",
                borderRadius: "10px",
                overflow: "hidden"
              }}
            >
              <div
                style={{
                  width: progressWidth,
                  height: "100%",
                  background:
                    "linear-gradient(90deg, #0dcaf0 0%, #d6258f 100%)",
                  borderRadius: "10px",
                  transition: "width 0.5s ease-in-out"
                }}
              ></div>
            </div>

            <div
              className="card shadow border-0 w-100"
              style={{
                maxWidth: "800px",
                borderRadius: "12px",
                overflow: "hidden"
              }}
            >
              <div className="pi-card-header text-white text-center p-4">
                <h2 className="h4 fw-bold mb-1">
                  {step === Dental_Enrollment_Steps.practiceInfo
                    ? "Practice Information"
                    : "Services & Optional Details"}
                </h2>
                <p className="small mb-0 opacity-75">
                  {step === Dental_Enrollment_Steps.practiceInfo
                    ? "Tell us about your dental practice"
                    : "Share your services and additional information"}
                </p>
              </div>

              <div className="card-body p-4 p-md-5">
                <form className="text-start" onSubmit={handleSubmit}>
                  {step === Dental_Enrollment_Steps.practiceInfo && (
                    <DentistEnrollmentPracticeInfo
                      formData={formData}
                      handleChange={handleChange}
                      onNext={nextStep}
                      errors={errors}
                    />
                  )}

                  {step === Dental_Enrollment_Steps.serviceInfo && (
                    <DentistEnrollmentServiceInfo
                      formData={formData}
                      handleChange={handleChange}
                      handleMultiSelect={handleMultiSelect}
                      onBack={prevStep}
                      handleFileChange={handleFileChange}
                      handleRemoveFile={handleRemoveFile}
                      errors={errors}
                    />
                  )}

                  <div className="text-center small text-muted">
                    Read more about our policies: <br />
                    <a
                      href="https://dental.com/terms-and-conditions-of-use"
                      className="text-decoration-underline"
                      style={{ color: "#9d3bf0" }}
                      target="_blank"
                    >
                      Terms and Conditions
                    </a>{" "}
                    •{" "}
                    <a
                      href="https://dental.com/privacy-practices"
                      className="text-decoration-underline"
                      style={{ color: "#9d3bf0" }}
                      target="_blank"
                    >
                      Privacy Practices
                    </a>
                  </div>
                </form>
              </div>
            </div>
          </>
        )}
    </div>
  );
};

export default DentistSignUpForm;
